import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { CalendarDays, CalendarPlus, Clock, Loader2, MapPin, XCircle, RefreshCw, CheckCircle2, Video } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { MonthCalendar } from "./Book";
import { useT } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookManage() {
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const { t } = useT();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("view"); // view | reschedule | cancel
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/booking/manage`, { params: { token } });
      setBooking(res.data);
    } catch {
      setError(t("bm.notFound"));
    }
  };

  useEffect(() => {
    if (token) load();
    else setError(t("bm.missingLink"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!date || mode !== "reschedule") return;
    setTime("");
    axios
      .get(`${API}/booking/availability`, { params: { date } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]));
  }, [date, mode]);

  const reschedule = async () => {
    if (!date || !time || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/booking/reschedule`, { token, date, time });
      setDone(`${t("bm.reschedule")} → ${date} · ${time} ET`);
      setMode("view");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || t("book.errorGeneric"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/booking/cancel-by-token`, { token });
      setDone(t("bm.policy"));
      setMode("view");
      await load();
    } catch {
      setError(t("bm.cancelFail"));
    } finally {
      setBusy(false);
    }
  };

  const active = booking && ["confirmed", "pending_payment"].includes(booking.status);

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="book-manage-page">
      <Header />
      <main className="max-w-2xl mx-auto px-5 py-12 sm:py-16">
        <h1 className="font-serif italic text-4xl text-[#3F5043] mb-2 text-center">{t("bm.title")}</h1>
        <p className="text-sm text-[#3F5043]/60 text-center mb-8">{t("bm.subtitle")}</p>

        {error && (
          <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-6 text-center text-sm text-red-700" data-testid="manage-error">
            {error}
          </div>
        )}

        {!booking && !error && (
          <div className="flex justify-center py-10">
            <Loader2 size={26} className="animate-spin text-[#899480]" />
          </div>
        )}

        {booking && (
          <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-6 sm:p-8" data-testid="manage-card">
            {done && (
              <div className="mb-5 flex items-start gap-2 bg-[#899480]/10 border border-[#899480]/30 rounded-2xl p-4 text-sm text-[#3F5043]" data-testid="manage-done">
                <CheckCircle2 size={16} className="text-[#899480] mt-0.5 shrink-0" /> {done}
              </div>
            )}

            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <div className="font-serif text-2xl text-[#3F5043]">{booking.title}</div>
                <div className="text-sm text-[#3F5043]/60">{booking.guest_name || booking.client_email}</div>
              </div>
              <span
                data-testid="manage-status"
                className={`text-[11px] uppercase tracking-wider font-semibold px-3 py-1 rounded-full ${
                  booking.status === "confirmed"
                    ? "bg-[#899480]/15 text-[#3F5043]"
                    : booking.status === "canceled"
                      ? "bg-red-100 text-red-700"
                      : "bg-[#AE8F78]/15 text-[#875F45]"
                }`}
              >
                {booking.status === "pending_payment" ? t("bm.pending")
                  : booking.status === "pending_review" ? "Under review"
                  : booking.status === "awaiting_payment" ? "Awaiting payment"
                  : booking.status}
              </span>
            </div>

            <div className="space-y-2 text-sm text-[#3F5043]/80 mb-6">
              <div className="flex items-center gap-2">
                <CalendarDays size={15} className="text-[#AE8F78]" /> {booking.date}
              </div>
              <div className="flex items-center gap-2">
                <Clock size={15} className="text-[#AE8F78]" /> {booking.time} ET
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={15} className="text-[#AE8F78]" /> {booking.location}
              </div>
            </div>

            {booking.status === "awaiting_payment" && booking.checkout_url && (
              <a
                href={booking.checkout_url}
                data-testid="manage-complete-payment"
                className="mb-6 flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-semibold hover:bg-[#875F45] transition-colors"
              >
                <CheckCircle2 size={15} /> Complete your payment to confirm
              </a>
            )}

            {booking.status === "pending_review" && (
              <div data-testid="manage-pending-review" className="mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-2xl p-4 text-sm text-[#3F5043]/80 text-center">
                Our team is reviewing your request — you'll receive a text and email with a secure
                payment link as soon as it's accepted. No charge has been made.
              </div>
            )}

            {booking.gcal_url && booking.status === "confirmed" && (
              <a
                href={booking.gcal_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="manage-add-calendar"
                className="mb-6 flex items-center justify-center gap-2 rounded-full px-6 py-3 border border-[#3F5043]/30 text-[#3F5043] text-sm font-semibold hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors"
              >
                <CalendarPlus size={15} /> Add to Google Calendar
              </a>
            )}

            {(booking.is_video || booking.service === "video-consult") && booking.status === "confirmed" && (
              <Link
                to={`/video-visit/${booking.id}?token=${token}`}
                data-testid="manage-join-video"
                className="mb-6 flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#D4AF37] text-[#3F5043] text-sm font-semibold hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors"
              >
                <Video size={15} /> Join your video visit
              </Link>
            )}

            {active && mode === "view" && (
              <>
                <div className="bg-[#875F45]/5 border border-[#875F45]/20 rounded-2xl p-4 text-xs text-[#3F5043]/80 leading-relaxed mb-5">
                  <strong className="text-[#875F45]">{t("book.policyStrong")}</strong> {t("bm.policy")}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="manage-reschedule-btn"
                    onClick={() => setMode("reschedule")}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
                  >
                    <RefreshCw size={14} /> {t("bm.reschedule")}
                  </button>
                  <button
                    data-testid="manage-cancel-btn"
                    onClick={() => setMode("cancel")}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 border border-[#875F45]/40 text-[#875F45] text-sm font-medium hover:bg-[#875F45]/5 transition-colors"
                  >
                    <XCircle size={14} /> {t("bm.cancel")}
                  </button>
                </div>
              </>
            )}

            {mode === "reschedule" && (
              <div data-testid="manage-reschedule-panel">
                <MonthCalendar selected={date} onSelect={setDate} />
                {date && (
                  <div className="mt-4">
                    {slots === null ? (
                      <Loader2 size={16} className="animate-spin text-[#899480]" />
                    ) : slots.length === 0 ? (
                      <p className="text-sm text-[#3F5043]/60">{t("bm.noSlots")}</p>
                    ) : (
                      <div className="grid grid-cols-4 gap-2">
                        {slots.map((t) => (
                          <button
                            key={t}
                            data-testid={`manage-slot-${t}`}
                            onClick={() => setTime(t)}
                            className={`rounded-xl px-2 py-2 text-sm transition-colors ${
                              time === t ? "bg-[#3F5043] text-[#F8F7F3] font-semibold" : "bg-[#F8F7F3] text-[#3F5043] hover:bg-[#899480]/20"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-3 mt-5">
                  <button
                    data-testid="manage-confirm-reschedule"
                    disabled={!date || !time || busy}
                    onClick={reschedule}
                    className="flex-1 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40"
                  >
                    {busy ? "…" : t("bm.confirmNew")}
                  </button>
                  <button
                    onClick={() => setMode("view")}
                    className="rounded-full px-6 py-3 text-sm text-[#3F5043]/60 hover:text-[#3F5043]"
                  >
                    {t("bm.back")}
                  </button>
                </div>
              </div>
            )}

            {mode === "cancel" && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5" data-testid="manage-cancel-panel">
                <p className="text-sm text-red-800 leading-relaxed mb-4">
                  <strong>{t("book.policyStrong")}</strong> {t("bm.cancelWarn")}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="manage-confirm-cancel"
                    disabled={busy}
                    onClick={cancel}
                    className="rounded-full px-6 py-3 bg-red-700 text-white text-sm font-medium hover:bg-red-800 transition-colors disabled:opacity-40"
                  >
                    {busy ? "…" : t("bm.yesCancel")}
                  </button>
                  <button
                    data-testid="manage-choose-reschedule"
                    onClick={() => setMode("reschedule")}
                    className="rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
                  >
                    {t("bm.rescheduleInstead")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-8">
          <Link to="/" className="text-xs text-[#3F5043]/60 hover:text-[#3F5043]">
            {t("book.back")}
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
